import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { ROUTES } from '../App';
import { LoadingSpinner } from './ui/loading-spinner';

const ProtectedRoute = ({ 
  children, 
  requiredSubscription, 
  subscriptionStatus,
  currentUser // Add currentUser prop
}) => {
  const auth = getAuth();
  const location = useLocation();

  //console.log(`[ProtectedRoute] Path: ${location.pathname}`);
  //console.log("[ProtectedRoute] Received currentUser:", JSON.parse(JSON.stringify(currentUser || {})));
  //console.log("[ProtectedRoute] Received subscriptionStatus:", JSON.parse(JSON.stringify(subscriptionStatus || {})));

  // Check if user is authenticated
  if (!auth.currentUser) {
    //console.log("[ProtectedRoute] No authenticated user. Redirecting to HOME.");
    return <Navigate to={ROUTES.HOME} replace />;
  }

  // Check if onboarding is complete (only for routes that aren't part of onboarding)
  const isOnboardingPath = [ROUTES.HOME, ROUTES.PROFILE, ROUTES.BILLING].includes(location.pathname);
  
  // ADDED: Special handling for Practice Manager profile access
  const isPracticeManagerProfileAccess = currentUser?.role === 'Practice Manager' && location.pathname === ROUTES.PROFILE;
  
  const consentGiven = currentUser?.consentGiven === 'true';
  const profileComplete = currentUser?.profileComplete === true;
  const roleSelected = currentUser?.role && currentUser?.role.trim() !== '';
  
  // ADDED: Practice Manager role logic
  const isPracticeManager = currentUser?.role === 'Practice Manager';
  
  // Extract subscription data with proper null checks
  const subscription = subscriptionStatus?.subscription || subscriptionStatus;
  
  // Fix the subStatusActive check
  const subStatusActive = subscription && 
    (subscription.status === 'active' || 
     subscription.status === 'trialing' || 
     subscription.status === 'past_due') && // ADDED: Allow past_due users to access billing
    (subscription.level === 'basic' || subscription.level === 'pro');
  const billingCompleteFlag = currentUser?.billingComplete === true;
  const onboardingCompleteFlag = currentUser?.onboardingComplete === true;

  // MODIFIED: Role-aware onboarding completion check
  // Practice Managers don't need billing, others do
  const needsBilling = !isPracticeManager;
  const billingRequirementMet = !needsBilling || subStatusActive || billingCompleteFlag;
  const isOnboardingComplete = onboardingCompleteFlag || 
    (consentGiven && profileComplete && roleSelected && billingRequirementMet);

  if (process.env.NODE_ENV === 'development') {
    console.log("[ProtectedRoute] Onboarding check values:", {
      consentGiven,
      profileComplete,
      roleSelected,
      isPracticeManager,
      needsBilling,
      billingRequirementMet, // ADDED: Log this for debugging
      subStatusActive,
      billingCompleteFlag,
      onboardingCompleteFlag,
      calculatedIsOnboardingComplete: isOnboardingComplete,
      isOnboardingPath,
      isPracticeManagerProfileAccess
    });
  }

  // MODIFIED: Allow Practice Managers to access profile even with incomplete onboarding
  if (!isOnboardingComplete && !isOnboardingPath && !isPracticeManagerProfileAccess) {
   // console.log('[ProtectedRoute] Onboarding NOT complete AND not an onboarding path. Redirecting...');
    
    // Determine which step the user needs to complete
    if (!consentGiven) {
      //console.log("[ProtectedRoute] Redirecting to HOME for consent.");
      return <Navigate to={ROUTES.HOME} replace />;
    }
    
    if (!profileComplete || !roleSelected) {
     // console.log("[ProtectedRoute] Redirecting to PROFILE for profile/role completion.");
      return <Navigate to={ROUTES.PROFILE} replace state={{ onboarding: true }} />;
    }
    
    // MODIFIED: Only redirect to billing if user needs billing (not Practice Manager)
    if (needsBilling) {
      //console.log("[ProtectedRoute] Redirecting to BILLING for billing completion.");
      return <Navigate to={ROUTES.BILLING} replace state={{ onboarding: true, forceShowPlans: true }} />;
    }
  }

  // Check for subscription requirements
  if (requiredSubscription && 
      (!subscriptionStatus || 
       !subscriptionStatus.subscription || // Check if subscription object exists
       // CRITICAL FIX: Exclude past_due from pro feature blocking - allow access to billing
       !(subscriptionStatus.subscription.status === 'active' || 
         subscriptionStatus.subscription.status === 'trialing' ||
         (subscriptionStatus.subscription.status === 'past_due' && location.pathname === '/billing')) || 
       subscriptionStatus.subscription.level !== requiredSubscription)) {
    //console.log(`[ProtectedRoute] Subscription requirement not met for Pro feature. Required: ${requiredSubscription}, User level: ${subscriptionStatus?.subscription?.level}, Status: ${subscriptionStatus?.subscription?.status}. Redirecting to BILLING.`);
    return <Navigate to={ROUTES.BILLING} replace />;
  }

  //console.log("[ProtectedRoute] All checks passed. Rendering children.");
  return children;
};

export default ProtectedRoute;